import {BrowserRouter, Routes, Route} from 'react-router-dom'
import {Layout, ProtectedRoute, MessageBox, Loading, ClientLayout, CaregiverLayout, LogoutDialog, AdminLayout} from './components'
import {
    Login,
    BlankPage,
    AccountSettings,
    OrganizationSettings,
    TeamManagement,
    EditProfile,
    EditOrganizationSettings,
    RoleSettings,
    RoleForm,
    Welcome,
    Dashboard,
    ClientsManagement,
    CaregiversManagement,
    ClientForm,
    ClientPersonalInformation,
    CaregiverPersonalInformation,
    ClientContacts,
    ClientServiceAgreement,
    ClientServiceAgreementForm,
    ClientViewServiceAgreement,
    ClientViewServiceAgreementDetails,
    CaregiverForm,
    BranchManagement,
    CaregiverService,
    CaregiverAssignedClient,
    CaregiverSchedule,
    ClientSchedule,
    Scheduling,
    ScheduleForm,
    ClientAssignedCaregiver,
    ScheduleDetails,
    Evv,
    EvvDetails,
    AdminOrganizationManagement,
    AdminOrganizationDetails,
    AdminOrganizationForm,
    AdminOrganizationAdd,
    AdminBranchForm,
} from './pages'
import {ThemeProvider, createTheme} from '@mui/material/styles'
import {appTheme} from './utils'
import {AuthProvider, useAuth} from './contexts/AuthContext'
import {GlobalStatesProvider} from './contexts/GlobalStatesContext'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {enUS} from 'date-fns/locale'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import customParseFormat from 'dayjs/plugin/customParseFormat'

export default () => {
    const theme = createTheme(appTheme)
    dayjs.extend(weekday)
    dayjs.extend(localeData)
    dayjs.extend(customParseFormat)

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                <GlobalStatesProvider>
                    <AuthProvider>
                        <Navigation />
                        <MessageBox />
                        <Loading />
                        <LogoutDialog />
                    </AuthProvider>
                </GlobalStatesProvider>
            </LocalizationProvider>
        </ThemeProvider>
    )
}

const Navigation = () => {
    const {permissions} = useAuth()

    const routes = [
        {path: '/', page: <Dashboard />, permissions},
        {path: '/clients-management', page: <ClientsManagement />, permissions: ['clients.read']},
        {path: '/clients-management/client-form', page: <ClientForm />, permissions: ['clients.write']},
        {
            path: '/clients-management-nosublayout/:clientId/service-agreement/service-agreement-form',
            page: <ClientServiceAgreementForm />,
            permissions: ['clients.write'],
        },
        {
            path: '/clients-management-nosublayout/:clientId/service-agreement/service-agreement-form/:serviceAgreementId',
            page: <ClientServiceAgreementForm />,
            permissions: ['clients.write'],
        },
        {
            path: '/clients-management-nosublayout/:clientId/service-agreement/view-service-agreement/:serviceAgreementId',
            page: <ClientViewServiceAgreement />,
            permissions: ['clients.write'],
        },
        {
            path: '/clients-management-nosublayout/:clientId/service-agreement/view-service-agreement/:serviceAgreementId/view-service-agreement-details/:serviceId',
            page: <ClientViewServiceAgreementDetails />,
            permissions: ['clients.write'],
        },
        {path: '/caregivers-management', page: <CaregiversManagement />, permissions: ['employees.read']},
        {path: '/caregivers-management/caregiver-form', page: <CaregiverForm />, permissions: ['employees.write']},
        {path: '/incoming-clients', page: <BlankPage />, permissions: ['clients.read']},
        {path: '/current-clients', page: <BlankPage />, permissions: ['clients.read']},
        {path: '/find-caregivers', page: <BlankPage />, permissions: ['employees.read']},
        {path: '/caregivers-list', page: <BlankPage />, permissions: ['employees.read']},
        {path: '/payout', page: <BlankPage />, permissions: ['employees.read']},
        {path: '/service-management', page: <BlankPage />, permissions: ['services.read']},
        {path: '/scheduling', page: <Scheduling />, permissions: ['schedules.read']},
        {path: '/scheduling/schedule-form', page: <ScheduleForm />, permissions: ['schedules.write']},
        {path: '/scheduling/schedule-form/:scheduleId', page: <ScheduleForm />, permissions: ['schedules.write']},
        {path: '/scheduling/:scheduleId/visits/:visitId', page: <ScheduleDetails />, permissions: ['schedules.write']},
        {path: '/evv', page: <Evv />, permissions: ['evv.read']},
        {path: '/evv/:visitId', page: <EvvDetails />, permissions: ['evv.read']},
        {path: '/billing', page: <BlankPage />, permissions: ['billing.read']},
        {path: '/analytics', page: <BlankPage />, permissions: ['reports.financial', 'reports.hr', 'reports.clinical', 'reports.compliance']},
        {path: '/settings', page: <BlankPage />, permissions},
        {path: '/help-and-support', page: <BlankPage />, permissions},
        {path: '/account-settings', page: <AccountSettings />, permissions},
        {path: '/account-settings/edit-profile', page: <EditProfile />, permissions: ['admin.settings.write']},
        {path: '/organization-settings', page: <OrganizationSettings />, permissions: ['admin.settings.read']},
        {path: '/organization-settings/edit-organization-settings', page: <EditOrganizationSettings />, permissions: ['admin.settings.write']},
        {path: '/branch-management', page: <BranchManagement />, permissions: ['admin.settings.read']},
        {path: '/team-management', page: <TeamManagement />, permissions: ['admin.users.read', 'admin.roles.read']},
        {path: '/team-management/role-settings', page: <RoleSettings />, permissions: ['admin.users.write', 'admin.roles.write']},
        {path: '/team-management/role-settings/role-form', page: <RoleForm />, permissions: ['admin.users.write', 'admin.roles.write']},
        {path: '/team-management/role-settings/role-form/:roleId', page: <RoleForm />, permissions: ['admin.users.write', 'admin.roles.write']},
        {path: '/integration-settings', page: <BlankPage />, permissions: ['admin.integrations']},
    ]

    const clientRoutes = [
        {path: '/clients-management/:clientId/schedule', page: <ClientSchedule />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/personal-information', page: <ClientPersonalInformation />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/client-contacts', page: <ClientContacts />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/medical-information', page: <BlankPage />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/assigned-caregiver', page: <ClientAssignedCaregiver />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/active-services', page: <BlankPage />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/service-agreement', page: <ClientServiceAgreement />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/billing-history', page: <BlankPage />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/documents', page: <BlankPage />, permissions: ['clients.write']},
        {path: '/clients-management/:clientId/credentials', page: <BlankPage />, permissions: ['clients.write']},
    ]

    const adminRoutes = [
        {path: '/admin', page: <AdminOrganizationManagement />, permissions},
        {path: '/admin/organizations', page: <AdminOrganizationManagement />, permissions},
        {path: '/admin/organizations/add', page: <AdminOrganizationAdd />, permissions},
        {path: '/admin/organizations/:organizationId', page: <AdminOrganizationDetails />, permissions},
        {path: '/admin/organizations/:organizationId/edit', page: <AdminOrganizationForm />, permissions},
        {path: '/admin/organizations/:organizationId/branches/add', page: <AdminBranchForm />, permissions},
        {
            path: '/admin/organizations/:organizationId/branches/:branchId/edit',
            page: <AdminBranchForm />,
            permissions,
        },
    ]

    const caregiverRoutes = [
        {path: '/caregivers-management/:caregiverId/schedule', page: <CaregiverSchedule />, permissions: ['employees.write']},
        {path: '/caregivers-management/:caregiverId/service', page: <CaregiverService />, permissions: ['employees.write']},
        {path: '/caregivers-management/:caregiverId/personal-information', page: <CaregiverPersonalInformation />, permissions: ['employees.write']},
        {path: '/caregivers-management/:caregiverId/assigned-client', page: <CaregiverAssignedClient />, permissions: ['employees.write']},
        {path: '/caregivers-management/:caregiverId/documents', page: <BlankPage />, permissions: ['employees.write']},
    ]

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/welcome" element={<ProtectedRoute routePermissions={permissions}>{<Welcome />}</ProtectedRoute>} />
                <Route element={<Layout />}>
                    {routes.map(route => (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={<ProtectedRoute routePermissions={route.permissions}>{route.page}</ProtectedRoute>}
                        />
                    ))}
                    <Route element={<ClientLayout />}>
                        {clientRoutes.map(route => (
                            <Route
                                key={route.path}
                                path={route.path}
                                element={<ProtectedRoute routePermissions={route.permissions}>{route.page}</ProtectedRoute>}
                            />
                        ))}
                    </Route>
                    <Route element={<CaregiverLayout />}>
                        {caregiverRoutes.map(route => (
                            <Route
                                key={route.path}
                                path={route.path}
                                element={<ProtectedRoute routePermissions={route.permissions}>{route.page}</ProtectedRoute>}
                            />
                        ))}
                    </Route>
                </Route>
                <Route element={<AdminLayout />}>
                    {adminRoutes.map(route => (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={<ProtectedRoute routePermissions={route.permissions}>{route.page}</ProtectedRoute>}
                        />
                    ))}
                </Route>
            </Routes>
        </BrowserRouter>
    )
}
